import * as React from 'react';
import Config from 'config';
import { Box } from '@apia/theme';
import DialogButtonBar from './DialogButtonBar';
import SimpleButton from '../../buttons/SimpleButton';
import { TApiaButtonType } from '../../../../types';
import { getSpecificParent } from '../../../../utils/dom';
import { ModalContext } from '@apia/components';

export function getConfirmButton(ref: HTMLElement) {
  return ref?.querySelector('button[type="submit"]') as HTMLElement;
}

export interface IConfirm {
  additionalButtons?: React.ReactNode;
  additionalButtonsOnRight?: React.ReactNode;
  cancelButtonText?: string;
  children: React.ReactNode;
  className?: string;
  confirmButtonText?: string;
  confirmButtonVariant?: TApiaButtonType;
  contentRef?: React.Ref<HTMLDivElement>;
  disabled?: boolean;
  hideCancelButton?: boolean;
  hideConfirmButton?: boolean;
  isLoading?: boolean;
  onConfirm?: (ev: React.MouseEvent) => unknown;
  onCancel?: (ev: React.MouseEvent) => unknown;
  variant?: string;
}

const Confirm = ({
  additionalButtons,
  additionalButtonsOnRight,
  children,
  confirmButtonText: confirmText = Config.BTN_CONFIRM,
  cancelButtonText: cancelText = Config.BTN_CANCEL,
  confirmButtonVariant: confirmType = 'primary',
  disabled,
  hideCancelButton,
  hideConfirmButton,
  onConfirm: onConfirmOk,
  onCancel: onConfirmCancel,
  isLoading,
  contentRef,
  className,
  variant,
}: IConfirm): JSX.Element => {
  const { descriptionId } = React.useContext(ModalContext)!;

  return (
    <Box
      {...window.getVariant(variant ?? 'layout.common.modals.confirm')}
      className={`${className ?? ''} confirm`}
    >
      <Box ref={contentRef} className="confirm__content" id={descriptionId}>
        {children}
      </Box>
      <DialogButtonBar className="confirm__buttonBar">
        <Box
          className="confirm__customButtons"
          onClick={React.useCallback(
            (ev: React.MouseEvent) => {
              if (
                getSpecificParent(
                  ev.target as HTMLElement,
                  (current) => current.tagName === 'BUTTON',
                )
              )
                onConfirmCancel?.(ev);
            },
            [onConfirmCancel],
          )}
        >
          {additionalButtons}
        </Box>

        <Box className="confirm__defaultButtons">
          <Box
            className="confirm__customButtonsOnRight"
            onClick={React.useCallback(
              (ev: React.MouseEvent) => {
                if (
                  getSpecificParent(
                    ev.target as HTMLElement,
                    (current) => current.tagName === 'BUTTON',
                  )
                )
                  onConfirmCancel?.(ev);
              },
              [onConfirmCancel],
            )}
          >
            {additionalButtonsOnRight}
          </Box>
          {!hideConfirmButton && (
            <SimpleButton
              onClick={onConfirmOk}
              title={confirmText}
              variant={confirmType}
              disabled={isLoading || disabled}
              isLoading={isLoading}
              className="confirm__confirmButton"
              data-test-id="confirm"
              type="submit"
            >
              {confirmText}
            </SimpleButton>
          )}
          {onConfirmCancel && !hideCancelButton && (
            <SimpleButton
              onClick={onConfirmCancel}
              title={cancelText}
              {...window.getVariant('secondary')}
              disabled={isLoading}
              className="confirm__cancelButton"
              data-test-id="cancel"
            >
              {cancelText}
            </SimpleButton>
          )}
        </Box>
      </DialogButtonBar>
    </Box>
  );
};

export default Confirm;
